import { gql, useApolloClient, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';

import { useCurrent } from '@/hooks/useCurrent';
import { logError } from '@/imports/logging/ClientLogger';
import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';

import {
  useEditCurrentUserGroupMembership_CreateGroupLinkMutation,
  useEditCurrentUserGroupMembership_CreateGroupLinkMutationVariables,
} from './graphql-types/useEditCurrentUserGroupMembership_CreateGroupLinkMutation';
import {
  useEditCurrentUserGroupMembership_DeleteGroupLinkMutation,
  useEditCurrentUserGroupMembership_DeleteGroupLinkMutationVariables,
} from './graphql-types/useEditCurrentUserGroupMembership_DeleteGroupLinkMutation';
import { useEditCurrentUserGroupMembership_GroupFragment } from './graphql-types/useEditCurrentUserGroupMembership_GroupFragment';

const FOLLOW_GROUP = gql`
  mutation useEditCurrentUserGroupMembership_CreateGroupLinkMutation(
    $input: CreateUserGroupLinkInput!
  ) {
    createUserGroupLink(input: $input) {
      data {
        _id
        user {
          _id
          userOnboardingChecklistState {
            userHasJoinedAGroup
          }
        }
        group {
          _id
          currentUserGroupLink {
            _id
          }
          parentGroup {
            _id
            currentUserGroupLink {
              _id
            }
          }
        }
      }
    }
  }
`;

const UNFOLLOW_GROUP = gql`
  mutation useEditCurrentUserGroupMembership_DeleteGroupLinkMutation(
    $input: DeleteUserGroupLinkInput!
  ) {
    deleteUserGroupLink(input: $input) {
      data {
        _id
        user {
          _id
          userOnboardingChecklistState {
            userHasJoinedAGroup
          }
        }
        group {
          currentUserGroupLink {
            _id
          }
          subgroups {
            groupNodes {
              group {
                _id
                currentUserGroupLink {
                  _id
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const useEditCurrentUserGroupMembership = (
  group: useEditCurrentUserGroupMembership_GroupFragment
): {
  createFollow: () => Promise<void>;
  deleteFollow: () => Promise<void>;
  userLoading: boolean;
  createFollowLoading: boolean;
  deleteFollowLoading: boolean;
} => {
  const current = useCurrent();
  const [createUserGroupLink, { loading: createFollowLoading }] = useMutation<
    useEditCurrentUserGroupMembership_CreateGroupLinkMutation,
    useEditCurrentUserGroupMembership_CreateGroupLinkMutationVariables
  >(FOLLOW_GROUP, {
    refetchQueries: [
      'GroupMembers_GroupUsersSearch',
      'VanillaNavigation_Query',
    ],
  });
  const [deleteUserGroupLink, { loading: deleteFollowLoading }] = useMutation<
    useEditCurrentUserGroupMembership_DeleteGroupLinkMutation,
    useEditCurrentUserGroupMembership_DeleteGroupLinkMutationVariables
  >(UNFOLLOW_GROUP, {
    refetchQueries: [
      'GroupMembers_GroupUsersSearch',
      'VanillaNavigation_Query',
    ],
  });

  const analyticsClient = useAnalyticsLogger();
  const apolloClient = useApolloClient();

  const toast = useToast();

  const createFollow = async (): Promise<void> => {
    if (!current.user?._id) {
      return;
    }
    try {
      await createUserGroupLink({
        variables: {
          input: {
            data: {
              userId: current.user._id,
              groupId: group._id,
            },
          },
        },
      });

      analyticsClient.logUserJoinedGroup();
      if (
        !!group.parentGroup?._id &&
        !group.parentGroup.currentUserGroupLink?._id
      ) {
        toast({
          title: `Successfully joined group "${group.name}" and its parent group "${group.parentGroup.name}".`,
          status: 'success',
        });
      } else {
        toast({
          title: `Successfully joined group "${group.name}".`,
          status: 'success',
        });
      }
    } catch (err) {
      toast({
        title: `Failed to join group "${group.name}".`,
        status: 'error',
      });
      logError(err);
    }
  };

  const deleteFollow = async (): Promise<void> => {
    try {
      await deleteUserGroupLink({
        variables: {
          input: { id: group.currentUserGroupLink?._id },
        },
      });

      const followedSubgroups = group.subgroups.groupNodes
        .filter((wrapper) => !!wrapper.group.currentUserGroupLink?._id)
        .map((group) => group.group._id);

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          id: group._id,
          __typename: 'Group',
        }),
        fields: {
          currentUserGroupLink() {
            return null;
          },
        },
      });
      followedSubgroups.forEach((subgroup) => {
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({
            id: subgroup,
            __typename: 'Group',
          }),
          fields: {
            currentUserGroupLink() {
              return null;
            },
          },
        });
      });
      apolloClient.cache.gc();
      analyticsClient.logUserLeftGroup();
      if (followedSubgroups.length > 0) {
        toast({
          title: `Successfully left group "${group.name}" and ${followedSubgroups.length} subgroups.`,
          status: 'success',
        });
      } else {
        toast({
          title: `Successfully left group "${group.name}".`,
          status: 'success',
        });
      }
    } catch (err) {
      toast({
        title: `Failed to leave group "${group.name}".`,
        status: 'error',
      });
      logError(err);
    }
  };

  return {
    createFollow,
    deleteFollow,
    userLoading: current.loading,
    createFollowLoading,
    deleteFollowLoading,
  };
};
useEditCurrentUserGroupMembership.groupFragment = gql`
  fragment useEditCurrentUserGroupMembership_GroupFragment on Group {
    _id
    name
    currentUserGroupLink {
      _id
    }
    currentUserGroupPermission {
      canJoin
    }
    parentGroup {
      _id
      name
      currentUserGroupLink {
        _id
      }
    }
    subgroups {
      groupNodes {
        group {
          _id
          currentUserGroupLink {
            _id
          }
        }
      }
    }
  }
`;
